// Internal modules
import type { ReactNode } from 'react';
import { getLocale } from '@/lib/globals';
import { appStore } from '@/store';
import { getContent } from '@/lib/content';
import {
  BILL_PAY_CONSENT,
  BILLING_WITH_PURCHASE_FLOW_TO_CONTENT_KEY_MAP,
} from './constants';
import { features } from '@/lib';
import {
  isChannelInitiatedBillingAgreement,
  isLegacySubscription,
  isMerchantInitiatedBillingAgreement,
  isNextGenSubscription,
  isUserActionCommit,
} from '@/lib/globals';
import { BillingType, CountryCodes as countrycodes } from '@/generated/graphql';
import type { CartState } from '@/store/models/api/types';
import type { CountryCodes } from '@/generated/graphql';
import type { AppMode, FiTypes } from '@/types';
import { APP_MODE } from '@/types';

const getBillingAgreementKey = (
  accountType: AppMode,
  billingType: BillingType,
  fiType: FiTypes,
): string => {
  const userAction = isUserActionCommit() ? 'commit' : 'continue';
  const { country } = getLocale();

  const {
    isChannelInitiatedBA,
    isBrProjectMacawNoBalanceFlow,
    isPaymentServiceActAppliedFlow,
    isRecurringPaymentsFlow,
    isBillingWithoutPurchase,
    isNextGenSubscriptionFlow,
  } = getFlowEligibility(country);

  if (isBrProjectMacawNoBalanceFlow) {
    return 'billingAgreement.noBalanceSignupAgreement';
  }

  if (isPaymentServiceActAppliedFlow) {
    return isChannelInitiatedBA
      ? `billingAgreement.${userAction}.channelInitiated`
      : `billingAgreement.${userAction}.merchantInitiated`;
  }

  if (isRecurringPaymentsFlow) {
    return getRecurringPaymentsKey(country, userAction);
  }

  if (isNextGenSubscriptionFlow) {
    return getNextGenSubscriptionKey(
      accountType,
      billingType,
      country,
      fiType,
      userAction,
    );
  }

  if (isBillingWithoutPurchase) {
    return getBAWithoutPurchaseContentKey(billingType, userAction);
  }

  // Billing Agreement With Purchase Flows
  // @ts-expect-error - note above
  const flowType = BILLING_WITH_PURCHASE_FLOW_TO_CONTENT_KEY_MAP[billingType];
  return `billingAgreement.${flowType}`;
};

type FlowEligibility = {
  isChannelInitiatedBA: boolean;
  isBrProjectMacawNoBalanceFlow: boolean;
  isPaymentServiceActAppliedFlow: boolean;
  isRecurringPaymentsFlow: boolean;
  isBillingWithoutPurchase: boolean;
  isNextGenSubscriptionFlow: boolean;
};

const getFlowEligibility = (country: CountryCodes): FlowEligibility => {
  const isChannelInitiatedBA = isChannelInitiatedBillingAgreement();
  const isMerchantInitiatedBA = isMerchantInitiatedBillingAgreement();

  /**
   * BR: In weasley there is no user.noBalance flag
   * Project Macaw: disables the disclaimer for BR users - all BA Flows
   */
  const isBrProjectMacawNoBalanceFlow = country === countrycodes.Br;

  /**
   * SG: Singapore Payment Service Act - Compliance Feature
   * BA with Purchase and without Purchase: MIB, MISB, CIB, and CISB
   */
  const isPaymentServiceActAppliedFlow =
    features.isActive('isPaymentServiceActApplied') &&
    (isChannelInitiatedBA || isMerchantInitiatedBA);

  const isRecurringPaymentsFlow = isLegacySubscription();

  const { isBillingWithoutPurchase } = appStore.getState().api;
  const isNextGenSubscriptionFlow =
    isNextGenSubscription() && isBillingWithoutPurchase;

  return {
    isBillingWithoutPurchase,
    isBrProjectMacawNoBalanceFlow,
    isChannelInitiatedBA,
    isNextGenSubscriptionFlow,
    isPaymentServiceActAppliedFlow,
    isRecurringPaymentsFlow,
  };
};

const getMerchantName = (
  cartDetails: CartState['cartDetails'],
): string | ReactNode => {
  return cartDetails.merchant.name || getContent('base.theMerchant');
};

const getBAWithoutPurchaseContentKey = (
  billingType: BillingType,
  userAction: string,
): string => {
  const { consentCode } = appStore.getState().api;
  const isBillPayConsent = consentCode === BILL_PAY_CONSENT; // Validation for billpay model 3 flow

  if (consentCode && consentCode !== '') {
    return isBillPayConsent
      ? 'enterpriseAgreement.billPayConsentMIBWithNoBalanceChoice'
      : `enterpriseAgreement.${consentCode}NoFiStickyConsentSignup`;
  }

  return getBAWithoutPurchaseDefaultKey(userAction, billingType);
};

const getBAWithoutPurchaseDefaultKey = (
  userAction: string,
  billingType: BillingType,
): string => {
  const action = userAction === 'commit' ? 'Pay' : 'Continue';
  const keySuffix = getKeySuffix(action, billingType);

  return `billingAgreement.signupAgreement${keySuffix}`;
};

const getKeySuffix = (action: string, type: BillingType): string => {
  const isCIB = type === BillingType.ChannelInitiatedBilling;
  // 'Fi' type applicable to both MIB and subscriptionsV1
  const contentBillingType = isCIB ? 'CibSticky' : 'FiSticky';
  const { country } = getLocale();
  const {
    cartDetails: { merchant },
  } = appStore.getState().api;
  const isIndianMerchant = merchant.country === countrycodes.In;

  if (country === countrycodes.In) {
    if (!isIndianMerchant && !isCIB) {
      return 'IndiaCrossBorderWithoutCib';
    } else if (isCIB) {
      return 'IndiaRtCib';
    } else if (isIndianMerchant && isReferenceTransaction(type)) {
      return 'IndiaDomesticRt';
    }
    return contentBillingType;
  }

  if (country === countrycodes.Us || isCIB) {
    return contentBillingType;
  }

  // Some countries share content in symlink'd country group folders
  // (GROUP-APAC/GROUP-LATAM/GROUP-EMEA), but have their own unique content.
  // To bypass the symlink, we are building the content key dynamically
  return getContent(
    `billingAgreement.signupAgreement${contentBillingType}${action}${country}`,
  )
    ? `${contentBillingType}${action}${country}`
    : `${contentBillingType}${action}`;
};

const isReferenceTransaction = (type: BillingType): boolean => {
  const isChannelInitiatedBilling =
    type === BillingType.ChannelInitiatedBilling;
  const isMerchantInitiatedBilling =
    type === BillingType.MerchantInitiatedBilling;
  const isMerchantInitiatedSingleBilling =
    type === BillingType.MerchantInitiatedSingleBilling;

  return Boolean(
    isChannelInitiatedBilling ||
      isMerchantInitiatedBilling ||
      isMerchantInitiatedSingleBilling,
  );
};

const getNextGenSubscriptionKey = (
  accountType: AppMode,
  billingType: BillingType,
  country: CountryCodes,
  fiType: FiTypes,
  userAction: string,
): string => {
  if (country === countrycodes.Sg) {
    if (userAction === 'continue') {
      return 'billingAgreement.guestAgreementSubscriptionV1Continue';
    }
    return 'billingAgreement.guestAgreementSubscriptionsV1';
  }
  if (accountType === APP_MODE.GUEST) {
    if (fiType === 'bank') {
      return 'billingAgreement.guestAgreementSubscriptionsV1Bank';
    }
    return 'billingAgreement.guestAgreementSubscriptionsV1';
  }

  // Using MIB BA text for subscription V1 signup flow
  return getBAWithoutPurchaseDefaultKey(userAction, billingType);
};

const getRecurringPaymentsKey = (
  country: CountryCodes,
  userAction: string,
): string => {
  const billingAgreementContentKey =
    'billingAgreement.signupRpAgreementFiSticky';
  if (country === countrycodes.Us) {
    return billingAgreementContentKey;
  }

  const action = userAction === 'commit' ? 'Pay' : 'Continue';
  // Some countries share content in symlink'd country group folders
  // (GROUP-APAC/GROUP-LATAM/GROUP-EMEA), but have their own unique content.
  // To bypass the symlink, we are building the content key dynamically
  return getContent(`${billingAgreementContentKey}${action}${country}`)
    ? `${billingAgreementContentKey}${action}${country}`
    : `${billingAgreementContentKey}${action}`;
};

export { getBillingAgreementKey, getMerchantName };
